import * as React from 'react';
import type { GameFacts, GameOutcome, Phase } from '@chess/api-model';
import { TextSkeleton } from '@chess/api-insights/charts';
import { shortDate } from './label.ts';
import { TILE_MIN_REM, pageDistance, scrollEdges, stripColumns } from './strip-layout.ts';
import type { DecidingStage } from '@chess/api-verdict';
import './game-strip.css';

// Home's game strip (2026-10-08), drawn from props only: one tile per game
// of the period, newest first, in a row that scrolls sideways; each tile
// gives the date, the result as a lettered badge (W, D, L in the result's
// colour), the reader's accuracy and the stage that decided the game (the
// verdict API's `decidingStage`, red when it went against the reader, green
// when for), so a game worth opening stands out while
// scanning back. The selected tile is outlined and kept in view. The row
// spans the dashboard's width (2026-10-10): as many whole tiles as fit share
// it evenly (`strip-layout.ts`), and ‹ › overlay its ends, shown on hover
// or keyboard focus where there is more to scroll to, each paging the row by
// its visible tiles. After the last tile, the period's
// count links to the library. Keyboard: ← and → step to the
// older and newer game, Home and End jump to the first and latest.

export type GameStripItem = Pick<GameFacts, 'id' | 'playedAt' | 'outcome' | 'opponent' | 'opponentRating' | 'scanned' | 'accuracy'> & { deciding: DecidingStage };

export type GameStripProps =
  | { loading: true }
  | {
      loading?: false;
      /** The period's games, newest first (the store's order). */
      games: readonly GameStripItem[];
      /** The selected game's index in `games`. */
      index: number;
      onSelect(id: string): void;
      /** Steps to the newer (-1) or the older (+1) game. */
      onStep(delta: number): void;
      /** Selects the oldest (`first`) or the latest (`last`) game. */
      onJump(edge: 'first' | 'last'): void;
      /** Where the count after the last tile goes; no link without it. */
      libraryHref?: string;
    };

const OUTCOME_WORD: Record<GameOutcome, string> = { win: 'Won', draw: 'Drawn', loss: 'Lost' };
const OUTCOME_LETTER: Record<GameOutcome, string> = { win: 'W', draw: 'D', loss: 'L' };
const STAGE_WORD: Record<Phase, string> = { opening: 'opening', middlegame: 'middlegame', ending: 'ending' };

const Mark: React.FC<{ deciding: DecidingStage }> = ({ deciding }) => {
  switch (deciding.kind) {
    case 'stage':
      return (
        <span className={`game-strip-mark ${deciding.pawns < 0 ? 'game-strip-mark-against' : 'game-strip-mark-for'}`} title={`The ${STAGE_WORD[deciding.stage]} decided it`}>
          <span className="game-strip-dot" aria-hidden="true" />
          {STAGE_WORD[deciding.stage]}
        </span>
      );
    case 'even':
      return <span className="game-strip-mark game-strip-mark-even">even</span>;
    case 'unscanned':
      return <span className="game-strip-mark game-strip-mark-unscanned">not scanned</span>;
  }
};

export const GameStrip: React.FC<GameStripProps> = (props) => {
  const selectedRef = React.useRef<HTMLButtonElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const index = props.loading ? -1 : props.index;
  const [columns, setColumns] = React.useState<number>();
  const [edges, setEdges] = React.useState({ back: false, forward: false });
  const loading = !!props.loading;
  const count = props.loading ? 0 : props.games.length;
  // Measures the row: how many tiles fit, and which ends have more to scroll to.
  React.useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      setColumns(stripColumns(row.clientWidth, TILE_MIN_REM * rem, gap));
      const next = scrollEdges(row.scrollLeft, row.clientWidth, row.scrollWidth);
      setEdges((e) => (e.back === next.back && e.forward === next.forward ? e : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    row.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      row.removeEventListener('scroll', measure);
    };
  }, [loading, count]);
  React.useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index]);
  const rowStyle = columns ? ({ '--strip-columns': columns } as React.CSSProperties) : undefined;

  if (props.loading) {
    return (
      <nav className="game-strip viz" aria-label="Games" aria-busy="true">
        <div ref={rowRef} className="game-strip-row" style={rowStyle}>
          {Array.from({ length: 8 }, (_, i) => (
            <span key={i} className="game-strip-tile" aria-hidden="true">
              <span className="game-strip-head"><span className="game-strip-date"><TextSkeleton sample="Sep 30" /></span></span>
              <span className="game-strip-accuracy"><TextSkeleton sample="85" /></span>
              <span className="game-strip-mark"><TextSkeleton sample="middlegame" /></span>
            </span>
          ))}
        </div>
      </nav>
    );
  }

  const { games, onSelect, onStep, onJump, libraryHref } = props;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.altKey || e.metaKey || e.ctrlKey) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      // Newest first: ← goes to the newer game on the left, → to the older on the right.
      onStep(e.key === 'ArrowLeft' ? -1 : 1);
    }
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      onJump(e.key === 'Home' ? 'last' : 'first');
    }
  };
  const countLabel = `${games.length} game${games.length === 1 ? '' : 's'}`;
  const scrollBy = (sign: 1 | -1) => {
    const row = rowRef.current;
    if (row) row.scrollBy({ left: sign * pageDistance(row.clientWidth, parseFloat(getComputedStyle(row).columnGap) || 0), behavior: 'smooth' });
  };
  return (
    <nav className="game-strip viz" aria-label="Games" onKeyDown={onKeyDown}>
      {/* The arrows are drawn over the row's ends (game-strip.css), taking none of its width. */}
      <button type="button" className={`game-strip-scroll game-strip-scroll-back${edges.back ? '' : ' game-strip-scroll-off'}`} aria-label="Scroll to newer games" onClick={() => scrollBy(-1)}>
        ‹
      </button>
      <div ref={rowRef} className="game-strip-row" style={rowStyle}>
        {games.map((game, i) => {
          const selected = i === index;
          const title = [shortDate(game.playedAt), OUTCOME_WORD[game.outcome], game.opponent ? `vs ${game.opponent}${game.opponentRating !== undefined ? ` ${game.opponentRating}` : ''}` : ''].filter(Boolean).join(' · ');
          return (
            <button
              key={game.id}
              ref={selected ? selectedRef : undefined}
              type="button"
              className={`game-strip-tile${selected ? ' game-strip-selected' : ''}`}
              aria-current={selected ? 'true' : undefined}
              title={title}
              tabIndex={selected ? 0 : -1}
              onClick={() => onSelect(game.id)}>
              <span className="game-strip-head">
                <span className="game-strip-date">{shortDate(game.playedAt)}</span>
                <span className={`game-strip-outcome game-strip-outcome-${game.outcome}`} role="img" aria-label={OUTCOME_WORD[game.outcome]}>
                  {OUTCOME_LETTER[game.outcome]}
                </span>
              </span>
              <span className="game-strip-accuracy" title="Accuracy">
                {game.accuracy === undefined ? <span className="game-strip-accuracy-none">—</span> : Math.round(game.accuracy)}
              </span>
              <Mark deciding={game.deciding} />
            </button>
          );
        })}
        {libraryHref ? (
          <a className="game-strip-tile game-strip-more" href={libraryHref}>
            {countLabel} ›
          </a>
        ) : (
          <span className="game-strip-tile game-strip-more">{countLabel}</span>
        )}
      </div>
      <button type="button" className={`game-strip-scroll game-strip-scroll-forward${edges.forward ? '' : ' game-strip-scroll-off'}`} aria-label="Scroll to older games" onClick={() => scrollBy(1)}>
        ›
      </button>
    </nav>
  );
};
